import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, RefreshCw, Eye, X, Clock, Layers, Smartphone,
  Globe, Zap, ChevronRight, ChevronDown, Monitor, AlertCircle,
  Bookmark, ShoppingCart, Heart, Package, Store, ExternalLink, ScanBarcode, Search, ShoppingBag, Bell, Send,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from 'recharts';
import {
  fetchRecentUsers, fetchUserTimeline, fetchUserBookmarks, fetchUserCart, fetchUserBarcodeScans, fetchUserSearches, fetchUserProductViews,
  fetchUserFcmTokens, sendUserNotification,
  RecentUser, UserTimeline, TimelineSession, UserBookmarkItem, UserCartItem, UserBarcodeScan, UserSearchKeyword, UserProductView,
} from '../services/api.ts';
import './UserActivity.css';

/* ── helpers ── */
const PAGE_LABELS: Record<string, string> = {
  home: 'Home',
  search: 'Search',
  product_detail: 'Product Details',
  posts: 'Posts',
  shopping_cart: 'Shopping Cart',
  bookmarks: 'Bookmarks',
  app_open: 'App Open',
  app_close: 'App Close',
};
const label = (p: string) => PAGE_LABELS[p] || p;

const PAGE_COLORS: Record<string, string> = {
  home: '#6366f1',
  search: '#8b5cf6',
  product_detail: '#ec4899',
  posts: '#f59e0b',
  shopping_cart: '#10b981',
  bookmarks: '#06b6d4',
  app_open: '#94a3b8',
  app_close: '#94a3b8',
};
const pageColor = (p: string) => PAGE_COLORS[p] || '#6b7280';

const fmtDuration = (s: number) => {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const speedClass = (ms: number) => (ms <= 500 ? 'speed-fast' : ms <= 1500 ? 'speed-ok' : 'speed-slow');

/* ── component ── */
const UserActivity: React.FC = () => {
  const [users, setUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [refreshing, setRefreshing] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [testAccountFilter, setTestAccountFilter] = useState<'all' | 'real' | 'test'>('real');

  // Derive unique countries from loaded users
  const countries = Array.from(new Set(users.map(u => u.country).filter(Boolean))).sort();
  const filteredUsers = countryFilter
    ? users.filter(u => u.country === countryFilter)
    : users;

  /* Modal */
  const [selectedUser, setSelectedUser] = useState<RecentUser | null>(null);
  const [timeline, setTimeline] = useState<UserTimeline | null>(null);
  const [tlLoading, setTlLoading] = useState(false);

  /* Modal tabs */
  type ModalTab = 'activity' | 'bookmarks' | 'cart' | 'barcodes' | 'searches' | 'product_views' | 'notifications';
  const [activeTab, setActiveTab] = useState<ModalTab>('activity');
  const [bookmarks, setBookmarks] = useState<UserBookmarkItem[]>([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [cart, setCart] = useState<UserCartItem[]>([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [barcodeScans, setBarcodeScans] = useState<UserBarcodeScan[]>([]);
  const [barcodesLoading, setBarcodesLoading] = useState(false);
  const [searches, setSearches] = useState<UserSearchKeyword[]>([]);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [productViews, setProductViews] = useState<UserProductView[]>([]);
  const [productViewsLoading, setProductViewsLoading] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  // Notification tab state
  const [notifEnabled, setNotifEnabled] = useState<boolean | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState<string | null>(null);

  const handleSendNotification = async () => {
    if (!selectedUser || !notifTitle.trim() || !notifBody.trim()) return;
    setNotifSending(true);
    setNotifResult(null);
    try {
      await sendUserNotification(selectedUser.user_id, notifTitle.trim(), notifBody.trim());
      setNotifResult('success');
      setNotifTitle('');
      setNotifBody('');
    } catch {
      setNotifResult('error');
    } finally {
      setNotifSending(false);
    }
  };

  const loadUsers = useCallback(async () => {
    try {
      setError(null);
      const testAccount = testAccountFilter === 'all' ? undefined : testAccountFilter === 'test';
      const data = await fetchRecentUsers(days, 20, testAccount);
      setUsers(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load user activity');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days, testAccountFilter]);

  useEffect(() => {
    setLoading(true);
    loadUsers();
  }, [loadUsers]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadUsers();
  };

  const openTimeline = async (user: RecentUser) => {
    setSelectedUser(user);
    setActiveTab('activity');
    setTlLoading(true);
    setTimeline(null);
    setBookmarks([]);
    setCart([]);
    setBarcodeScans([]);
    setSearches([]);
    setProductViews([]);
    setNotifEnabled(null);
    setNotifTitle('');
    setNotifBody('');
    setNotifResult(null);
    try {
      const data = await fetchUserTimeline(user.user_id, days);
      setTimeline(data);
    } catch {
      setTimeline(null);
    } finally {
      setTlLoading(false);
    }
  };

  const switchTab = async (tab: ModalTab) => {
    if (!selectedUser) return;
    setActiveTab(tab);

    if (tab === 'bookmarks' && bookmarks.length === 0 && !bookmarksLoading) {
      setBookmarksLoading(true);
      try {
        const data = await fetchUserBookmarks(selectedUser.user_id);
        setBookmarks(data);
      } catch {
        setBookmarks([]);
      } finally {
        setBookmarksLoading(false);
      }
    }

    if (tab === 'cart' && cart.length === 0 && !cartLoading) {
      setCartLoading(true);
      try {
        const data = await fetchUserCart(selectedUser.user_id);
        setCart(data);
      } catch {
        setCart([]);
      } finally {
        setCartLoading(false);
      }
    }

    if (tab === 'barcodes' && barcodeScans.length === 0 && !barcodesLoading) {
      setBarcodesLoading(true);
      try {
        const data = await fetchUserBarcodeScans(selectedUser.user_id, days);
        setBarcodeScans(data);
      } catch {
        setBarcodeScans([]);
      } finally {
        setBarcodesLoading(false);
      }
    }

    if (tab === 'searches' && searches.length === 0 && !searchesLoading) {
      setSearchesLoading(true);
      try {
        const data = await fetchUserSearches(selectedUser.user_id, days);
        setSearches(data);
      } catch {
        setSearches([]);
      } finally {
        setSearchesLoading(false);
      }
    }

    if (tab === 'product_views' && productViews.length === 0 && !productViewsLoading) {
      setProductViewsLoading(true);
      try {
        const data = await fetchUserProductViews(selectedUser.user_id, days);
        setProductViews(data);
      } catch {
        setProductViews([]);
      } finally {
        setProductViewsLoading(false);
      }
    }

    if (tab === 'notifications' && notifEnabled === null && !notifLoading) {
      setNotifLoading(true);
      setNotifTitle('');
      setNotifBody('');
      setNotifResult(null);
      try {
        const data = await fetchUserFcmTokens(selectedUser.user_id);
        setNotifEnabled(data.tokens.length > 0);
      } catch {
        setNotifEnabled(false);
      } finally {
        setNotifLoading(false);
      }
    }
  };

  const closeModal = () => {
    setSelectedUser(null);
    setTimeline(null);
    setBookmarks([]);
    setCart([]);
    setBarcodeScans([]);
    setSearches([]);
    setProductViews([]);
    setActiveTab('activity');
  };

  /* ── render ── */
  if (loading) {
    return (
      <div className="user-activity">
        <div className="ua-loading"><RefreshCw size={28} /> Loading…</div>
      </div>
    );
  }

  return (
    <div className="user-activity">
      {/* Header */}
      <div className="ua-header">
        <div className="ua-header-left">
          <h2><Users size={22} /> User Activity</h2>
          <span className="ua-subtitle">Last {filteredUsers.length} active users{countryFilter ? ` in ${countryFilter}` : ''}</span>
        </div>
        <div className="ua-header-right">
          <div className="ua-filter-group">
            <label>Country</label>
            <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}>
              <option value="">All Countries</option>
              {countries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="ua-filter-group">
            <label>Period</label>
            <select value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
          <div className="ua-filter-group">
            <label>Account</label>
            <select value={testAccountFilter} onChange={e => setTestAccountFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="real">Real Users</option>
              <option value="test">Test Accounts</option>
            </select>
          </div>
          <button className={`ua-btn-icon${refreshing ? ' spinning' : ''}`} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="ua-error">
          <AlertCircle size={18} />
          <p>{error}</p>
          <button className="ua-btn ua-btn-secondary" onClick={handleRefresh}>Retry</button>
        </div>
      )}

      {/* Users table */}
      <div className="ua-section">
        <table className="ua-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Country</th>
              <th>Platform</th>
              <th>Pages Browsed</th>
              <th>Sessions</th>
              <th>Total Time</th>
              <th>Avg Load</th>
              <th>Last Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr><td colSpan={9} className="ua-empty-row">No user activity found{countryFilter ? ` in ${countryFilter}` : ' in this period'}.</td></tr>
            ) : (
              filteredUsers.map(u => (
                <tr key={u.user_id}>
                  <td>
                    <div className="ua-user-cell">
                      <div className="ua-avatar">{(u.name || '?')[0].toUpperCase()}</div>
                      <div className="ua-user-info">
                        <span className="ua-user-name">{u.name || u.user_id}</span>
                        {u.email && <span className="ua-user-email">{u.email}</span>}
                      </div>
                    </div>
                  </td>
                  <td><span className="ua-country"><Globe size={14} /> {u.country}</span></td>
                  <td>
                    <span className="ua-platform-badge">
                      {u.platform === 'ios' ? '🍎' : u.platform === 'android' ? '🤖' : <Monitor size={14} />}
                      {' '}{u.platform || '—'}
                    </span>
                  </td>
                  <td>
                    <span className="ua-pages-count">{u.pages_browsed}</span>
                    <span className="ua-pages-unique">({u.unique_pages} unique)</span>
                  </td>
                  <td>{u.session_count}</td>
                  <td>{fmtDuration(u.total_duration_seconds)}</td>
                  <td>
                    <span className={`ua-speed-badge ${speedClass(u.avg_load_time_ms)}`}>
                      {Math.round(u.avg_load_time_ms)}ms
                    </span>
                  </td>
                  <td className="ua-last-active">{fmtDate(u.last_active)}</td>
                  <td>
                    <button className="ua-view-btn" onClick={() => openTimeline(u)}>
                      <Eye size={15} /> View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Timeline Modal */}
      {selectedUser && (
        <div className="ua-modal-overlay" onClick={closeModal}>
          <div className="ua-modal" onClick={e => e.stopPropagation()}>
            <div className="ua-modal-header">
              <div className="ua-modal-title">
                <div className="ua-avatar lg">{(selectedUser.name || '?')[0].toUpperCase()}</div>
                <div>
                  <h3>{selectedUser.name || selectedUser.user_id}</h3>
                  <span className="ua-modal-sub">
                    {selectedUser.email || selectedUser.user_id} · {selectedUser.platform} · {selectedUser.country}
                  </span>
                </div>
              </div>
              <button className="ua-modal-close" onClick={closeModal}><X size={20} /></button>
            </div>

            {/* Tab bar */}
            <div className="ua-tab-bar">
              <button
                className={`ua-tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => switchTab('activity')}
              >
                <Zap size={15} /> Activity
              </button>
              <button
                className={`ua-tab ${activeTab === 'bookmarks' ? 'active' : ''}`}
                onClick={() => switchTab('bookmarks')}
              >
                <Bookmark size={15} /> Bookmarks
                {bookmarks.length > 0 && <span className="ua-tab-count">{bookmarks.length}</span>}
              </button>
              <button
                className={`ua-tab ${activeTab === 'cart' ? 'active' : ''}`}
                onClick={() => switchTab('cart')}
              >
                <ShoppingCart size={15} /> Cart
                {cart.length > 0 && <span className="ua-tab-count">{cart.length}</span>}
              </button>
              <button
                className={`ua-tab ${activeTab === 'product_views' ? 'active' : ''}`}
                onClick={() => switchTab('product_views')}
              >
                <ShoppingBag size={15} /> Product Views
                {productViews.length > 0 && <span className="ua-tab-count">{productViews.length}</span>}
              </button>
              <button
                className={`ua-tab ${activeTab === 'searches' ? 'active' : ''}`}
                onClick={() => switchTab('searches')}
              >
                <Search size={15} /> Searches
                {searches.length > 0 && <span className="ua-tab-count">{searches.length}</span>}
              </button>
              <button
                className={`ua-tab ${activeTab === 'barcodes' ? 'active' : ''}`}
                onClick={() => switchTab('barcodes')}
              >
                <ScanBarcode size={15} /> Barcode Scans
                {barcodeScans.length > 0 && <span className="ua-tab-count">{barcodeScans.length}</span>}
              </button>
              <button
                className={`ua-tab ${activeTab === 'notifications' ? 'active' : ''}`}
                onClick={() => switchTab('notifications')}
              >
                <Bell size={15} /> Notification
              </button>
            </div>

            <div className="ua-modal-body">
              {/* ── Activity Tab ── */}
              {activeTab === 'activity' && (
                <>
              {tlLoading ? (
                <div className="ua-loading"><RefreshCw size={24} /> Loading timeline…</div>
              ) : !timeline || timeline.sessions.length === 0 ? (
                <div className="ua-empty">No activity data found.</div>
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="ua-modal-stats">
                    <div className="ua-mini-stat">
                      <Layers size={16} />
                      <div>
                        <span className="ua-mini-val">{timeline.summary.total_sessions}</span>
                        <span className="ua-mini-label">Sessions</span>
                      </div>
                    </div>
                    <div className="ua-mini-stat">
                      <Zap size={16} />
                      <div>
                        <span className="ua-mini-val">{timeline.summary.total_events}</span>
                        <span className="ua-mini-label">Events</span>
                      </div>
                    </div>
                    <div className="ua-mini-stat">
                      <Smartphone size={16} />
                      <div>
                        <span className="ua-mini-val">{timeline.summary.page_breakdown.length}</span>
                        <span className="ua-mini-label">Unique Pages</span>
                      </div>
                    </div>
                  </div>

                  {/* Page breakdown chart */}
                  <div className="ua-modal-section">
                    <h4>Page Visit Distribution</h4>
                    <div className="ua-chart-container">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={timeline.summary.page_breakdown.map(p => ({
                          name: label(p.page_name),
                          visits: p.visit_count,
                          avgLoad: p.avg_load_time_ms,
                          key: p.page_name,
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip
                            formatter={(val: number, name: string) =>
                              name === 'visits' ? [val, 'Visits'] : [`${val}ms`, 'Avg Load']
                            }
                          />
                          <Legend />
                          <Bar dataKey="visits" name="Visits" radius={[4, 4, 0, 0]}>
                            {timeline.summary.page_breakdown.map((p, i) => (
                              <Cell key={i} fill={pageColor(p.page_name)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Session timeline – most recent first */}
                  <div className="ua-modal-section">
                    <h4>Session Timeline (Most Recent First)</h4>
                    <div className="ua-sessions-list">
                      {[...timeline.sessions].reverse().map((session, si) => (
                        <SessionTimeline key={session.session_id} session={session} index={si} />
                      ))}
                    </div>
                  </div>
                </>
              )}
                </>
              )}

              {/* ── Bookmarks Tab ── */}
              {activeTab === 'bookmarks' && (
                <>
                  {bookmarksLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading bookmarks…</div>
                  ) : bookmarks.length === 0 ? (
                    <div className="ua-empty">
                      <Bookmark size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <p>No bookmarks found for this user.</p>
                    </div>
                  ) : (
                    <div className="ua-list-grid">
                      {bookmarks.map(b => (
                        <div key={b.id} className="ua-list-card">
                          <div className="ua-list-card-img">
                            {b.avater ? (
                              <img src={b.avater} alt={b.name} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            ) : (
                              <Package size={28} />
                            )}
                          </div>
                          <div className="ua-list-card-info">
                            <span className="ua-list-card-name">{b.name}</span>
                            <div className="ua-list-card-meta">
                              {b.lowest_price > 0 && (
                                <span className="ua-list-card-price">${b.lowest_price.toFixed(2)}</span>
                              )}
                              <span className="ua-list-card-detail">
                                <Heart size={12} /> {b.bookmark_count}
                              </span>
                              {b.is_feature && <span className="ua-list-card-badge">Featured</span>}
                            </div>
                            {b.created_at && (
                              <span className="ua-list-card-date">Bookmarked {fmtDate(b.created_at)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── Product Views Tab ── */}
              {activeTab === 'product_views' && (
                <>
                  {productViewsLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading product views…</div>
                  ) : productViews.length === 0 ? (
                    <div className="ua-empty">
                      <ShoppingBag size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <p>No product views found for this user.</p>
                    </div>
                  ) : (() => {
                    const grouped: Record<string, UserProductView[]> = {};
                    for (const v of productViews) {
                      const dayKey = v.viewed_at ? fmtDate(v.viewed_at) : 'Unknown date';
                      (grouped[dayKey] ??= []).push(v);
                    }
                    const toggleDay = (day: string) => {
                      setCollapsedDays(prev => {
                        const next = new Set(prev);
                        next.has(day) ? next.delete(day) : next.add(day);
                        return next;
                      });
                    };
                    return (
                      <div className="ua-product-views-list">
                        {Object.entries(grouped).map(([day, views]) => (
                          <div key={day} className="ua-product-views-day-group">
                            <div className="ua-day-header ua-day-header-clickable" onClick={() => toggleDay(day)}>
                              <ChevronDown size={14} className={`ua-day-chevron ${collapsedDays.has(day) ? 'collapsed' : ''}`} />
                              <Clock size={14} />
                              <span>{day}</span>
                              <span className="ua-day-count">{views.length} product{views.length !== 1 ? 's' : ''}</span>
                            </div>
                            {!collapsedDays.has(day) && <div className="ua-product-views-grid">
                              {views.map(v => (
                                <div key={v.id} className="ua-product-view-card">
                                  <div className="ua-product-view-img">
                                    {v.product_image ? (
                                      <img src={v.product_image} alt={v.product_name} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                    ) : (
                                      <Package size={28} />
                                    )}
                                  </div>
                                  <div className="ua-product-view-info">
                                    <span className="ua-product-view-name">{v.product_name}</span>
                                    <div className="ua-product-view-meta">
                                      <span className="ua-product-view-count">
                                        <Eye size={12} /> {v.view_count} view{v.view_count !== 1 ? 's' : ''}
                                      </span>
                                      {v.device && (
                                        <span className="ua-product-view-device">
                                          <Smartphone size={12} /> {v.device}
                                        </span>
                                      )}
                                      {v.os && (
                                        <span className="ua-product-view-os">
                                          <Monitor size={12} /> {v.os}
                                        </span>
                                      )}
                                    </div>
                                    {v.viewed_at && (
                                      <span className="ua-product-view-date">{fmtTime(v.viewed_at)}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}

              {/* ── Searches Tab ── */}
              {activeTab === 'searches' && (
                <>
                  {searchesLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading searches…</div>
                  ) : searches.length === 0 ? (
                    <div className="ua-empty">
                      <Search size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <p>No search keywords found for this user.</p>
                    </div>
                  ) : (
                    <div className="ua-search-list">
                      <table className="ua-table ua-search-table">
                        <thead>
                          <tr>
                            <th>Search Keyword</th>
                            <th>Results Found</th>
                            <th>Country</th>
                            <th>Searched At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searches.map(s => (
                            <tr key={s.id}>
                              <td>
                                <span className="ua-search-keyword">
                                  <Search size={14} /> {s.keyword}
                                </span>
                              </td>
                              <td>
                                <span className={`ua-search-results ${s.results_count === 0 ? 'zero' : ''}`}>
                                  {s.results_count}
                                </span>
                              </td>
                              <td>{s.country || '—'}</td>
                              <td className="ua-last-active">{s.searched_at ? fmtDate(s.searched_at) + ' ' + fmtTime(s.searched_at) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* ── Barcode Scans Tab ── */}
              {activeTab === 'barcodes' && (
                <>
                  {barcodesLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading barcode scans…</div>
                  ) : barcodeScans.length === 0 ? (
                    <div className="ua-empty">
                      <ScanBarcode size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <p>No barcode scans found for this user.</p>
                    </div>
                  ) : (
                    <div className="ua-barcode-list">
                      <table className="ua-table ua-barcode-table">
                        <thead>
                          <tr>
                            <th>Barcode</th>
                            <th>Results Found</th>
                            <th>Country</th>
                            <th>Scanned At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {barcodeScans.map(s => (
                            <tr key={s.id}>
                              <td>
                                <span className="ua-barcode-value">
                                  <ScanBarcode size={14} /> {s.barcode}
                                </span>
                              </td>
                              <td>
                                <span className={`ua-barcode-results ${s.results_count === 0 ? 'zero' : ''}`}>
                                  {s.results_count}
                                </span>
                              </td>
                              <td>{s.country || '—'}</td>
                              <td className="ua-last-active">{s.scanned_at ? fmtDate(s.scanned_at) + ' ' + fmtTime(s.scanned_at) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* ── Cart Tab ── */}
              {activeTab === 'cart' && (
                <>
                  {cartLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading cart…</div>
                  ) : cart.length === 0 ? (
                    <div className="ua-empty">
                      <ShoppingCart size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                      <p>No items in this user's cart.</p>
                    </div>
                  ) : (
                    <div className="ua-cart-list">
                      <div className="ua-cart-summary">
                        <span>{cart.length} item{cart.length !== 1 ? 's' : ''}</span>
                        <span className="ua-cart-total">
                          Total: ${cart.reduce((sum, c) => sum + c.price * c.quantity, 0).toFixed(2)}
                        </span>
                      </div>
                      {cart.map(c => (
                        <div key={c.shopping_id} className="ua-cart-item">
                          <div className="ua-cart-item-img">
                            {c.avater ? (
                              <img src={c.avater} alt={c.item_name} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            ) : (
                              <Package size={24} />
                            )}
                          </div>
                          <div className="ua-cart-item-info">
                            <span className="ua-cart-item-name">{c.itemkit_name || c.item_name}</span>
                            <span className="ua-cart-item-variant">{c.item_name}</span>
                            <div className="ua-cart-item-meta">
                              {c.retailer?.name && (
                                <span className="ua-cart-item-retailer">
                                  <Store size={12} /> {c.retailer.name}
                                </span>
                              )}
                              {c.added_by_user_name && (
                                <span className="ua-cart-item-addedby">Added by {c.added_by_user_name}</span>
                              )}
                              {c.created_at && (
                                <span className="ua-cart-item-date">{fmtDate(c.created_at)}</span>
                              )}
                            </div>
                          </div>
                          <div className="ua-cart-item-right">
                            <span className="ua-cart-item-qty">×{c.quantity}</span>
                            <span className="ua-cart-item-price">${c.price.toFixed(2)}</span>
                            {!c.is_available && <span className="ua-cart-item-unavailable">Unavailable</span>}
                            {c.url && (
                              <a href={c.url} target="_blank" rel="noreferrer" className="ua-cart-item-link">
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── Notification Tab ── */}
              {activeTab === 'notifications' && (
                <div className="ua-tab-content">
                  {notifLoading ? (
                    <div className="ua-loading"><RefreshCw size={20} className="spin" /> Checking notification status…</div>
                  ) : notifEnabled === false ? (
                    <div className="notif-disabled">
                      <Bell size={32} style={{ opacity: 0.3 }} />
                      <p>Notifications are disabled for this user.</p>
                      <span>This user has no active devices registered for push notifications.</span>
                    </div>
                  ) : notifEnabled ? (
                    <div className="notif-form">
                      {notifResult === 'success' && (
                        <div className="notif-result success">Notification sent successfully!</div>
                      )}
                      {notifResult === 'error' && (
                        <div className="notif-result error">Failed to send notification. Please try again.</div>
                      )}
                      <label className="notif-label">Title</label>
                      <input
                        className="notif-input"
                        type="text"
                        placeholder="Notification title"
                        value={notifTitle}
                        onChange={e => setNotifTitle(e.target.value)}
                        maxLength={100}
                      />
                      <label className="notif-label">Message</label>
                      <textarea
                        className="notif-textarea"
                        placeholder="Notification message"
                        value={notifBody}
                        onChange={e => setNotifBody(e.target.value)}
                        rows={4}
                        maxLength={500}
                      />
                      <button
                        className="notif-send-btn"
                        onClick={handleSendNotification}
                        disabled={notifSending || !notifTitle.trim() || !notifBody.trim()}
                      >
                        {notifSending ? <><RefreshCw size={16} className="spin" /> Sending…</> : <><Send size={16} /> Send Notification</>}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ── Session Timeline Sub-Component ── */
interface SessionTimelineProps {
  session: TimelineSession;
  index: number;
}

const SessionTimeline: React.FC<SessionTimelineProps> = ({ session, index }) => {
  const [expanded, setExpanded] = useState(index === 0);  // first session expanded by default

  const pageEvents = session.events.filter(e => e.page_name !== 'app_open' && e.page_name !== 'app_close');

  return (
    <div className="ua-session-block">
      <button className="ua-session-header" onClick={() => setExpanded(!expanded)}>
        <div className="ua-session-header-left">
          <ChevronRight size={16} className={`ua-chevron ${expanded ? 'open' : ''}`} />
          <Clock size={14} />
          <span className="ua-session-date">{fmtDate(session.start)}</span>
          <span className="ua-session-time">{fmtTime(session.start)} — {fmtTime(session.end)}</span>
        </div>
        <div className="ua-session-header-right">
          <span className="ua-session-meta">{pageEvents.length} pages</span>
          <span className="ua-session-meta">{fmtDuration(session.duration_seconds)}</span>
        </div>
      </button>

      {expanded && (
        <div className="ua-session-events">
          {/* Horizontal bar chart showing time on each page */}
          <div className="ua-session-chart">
            <ResponsiveContainer width="100%" height={Math.max(120, pageEvents.length * 36 + 40)}>
              <BarChart
                data={pageEvents.map(e => ({
                  name: label(e.page_name),
                  duration: e.time_on_page_seconds,
                  loadTime: e.load_time_ms,
                  key: e.page_name,
                }))}
                layout="vertical"
                margin={{ left: 100, right: 20, top: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} unit="s" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={90} />
                <Tooltip
                  formatter={(val: number, name: string) =>
                    name === 'duration' ? [`${val}s`, 'Time on Page'] : [`${val}ms`, 'Load Time']
                  }
                />
                <Bar dataKey="duration" name="Time on Page (s)" radius={[0, 4, 4, 0]}>
                  {pageEvents.map((e, i) => (
                    <Cell key={i} fill={pageColor(e.page_name)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Event list */}
          <div className="ua-event-list">
            {session.events.map((e, i) => (
              <div key={i} className={`ua-event ${e.page_name === 'app_open' || e.page_name === 'app_close' ? 'marker' : ''}`}>
                <div className="ua-event-dot" style={{ background: pageColor(e.page_name) }} />
                <div className="ua-event-time">{fmtTime(e.timestamp)}</div>
                <div className="ua-event-page">{label(e.page_name)}</div>
                {e.page_name !== 'app_open' && e.page_name !== 'app_close' && (
                  <>
                    <span className={`ua-event-load ${speedClass(e.load_time_ms)}`}>
                      {e.load_time_ms}ms
                    </span>
                    {e.time_on_page_seconds > 0 && (
                      <span className="ua-event-duration">{fmtDuration(e.time_on_page_seconds)}</span>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivity;
